"use client"

import { useSyncExternalStore, useCallback } from "react"
import type { FieldStatus, FieldMetadata, FieldStatusMap } from "@/lib/types/logos"
import type { SectionSlug } from "@/lib/types/sections"

interface FieldStatusState {
  sectionFieldStatuses: Record<SectionSlug, FieldStatusMap>
}

let state: FieldStatusState = {
  sectionFieldStatuses: {} as Record<SectionSlug, FieldStatusMap>,
}

const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return state
}

function getServerSnapshot(): FieldStatusState {
  return { sectionFieldStatuses: {} as Record<SectionSlug, FieldStatusMap> }
}

// Actions
function setFieldStatus(
  sectionSlug: SectionSlug,
  fieldPath: string,
  status: FieldStatus,
  extra: Partial<FieldMetadata> = {},
) {
  const sectionStatuses = state.sectionFieldStatuses[sectionSlug] || {}
  const existingMetadata = sectionStatuses[fieldPath] || {}

  const newMetadata: FieldMetadata = {
    ...existingMetadata,
    status,
    lastModifiedAt: new Date().toISOString(),
    lastModifiedBy: status === "ai_draft" ? "ai" : "human",
    ...extra,
  }

  state = {
    ...state,
    sectionFieldStatuses: {
      ...state.sectionFieldStatuses,
      [sectionSlug]: {
        ...sectionStatuses,
        [fieldPath]: newMetadata,
      },
    },
  }
  emitChange()
}

function markAsAIDraft(sectionSlug: SectionSlug, fieldPath: string, confidence: number, sourceSections: string[]) {
  setFieldStatus(sectionSlug, fieldPath, "ai_draft", {
    aiConfidence: confidence,
    aiSourceSections: sourceSections,
    lastModifiedBy: "ai",
  })
}

function markAsApproved(sectionSlug: SectionSlug, fieldPath: string) {
  setFieldStatus(sectionSlug, fieldPath, "approved", {
    humanReviewedAt: new Date().toISOString(),
    lastModifiedBy: "human",
  })
}

export function useFieldStatusStore() {
  const currentState = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return {
    sectionFieldStatuses: currentState.sectionFieldStatuses,
    setFieldStatus: useCallback(setFieldStatus, []),
    markAsAIDraft: useCallback(markAsAIDraft, []),
    markAsApproved: useCallback(markAsApproved, []),
    getFieldStatus: useCallback(
      (sectionSlug: SectionSlug, fieldPath: string) => currentState.sectionFieldStatuses[sectionSlug]?.[fieldPath],
      [currentState.sectionFieldStatuses],
    ),
    getSectionStatuses: useCallback(
      (sectionSlug: SectionSlug) => currentState.sectionFieldStatuses[sectionSlug] || {},
      [currentState.sectionFieldStatuses],
    ),
  }
}
